import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Building2, Users, FolderKanban, CreditCard, CheckCircle2, Clock, Plus, ChevronRight, Upload, Landmark, Paperclip, Search } from "lucide-react";

const STATUS_COLORS = {
    active: "text-[#0d1628] bg-[#b89b6f]/15 border-[#b89b6f]",
    pending: "text-orange-700 bg-orange-50 border-orange-200",
    paid_pending_invoice: "text-blue-700 bg-blue-50 border-blue-200",
    expired: "text-red-700 bg-red-50 border-red-200",
    cancelled: "text-gray-600 bg-gray-50 border-gray-200",
    none: "text-gray-600 bg-gray-50 border-gray-200",
};

const STATUS_LABELS = {
    active: "Ativa",
    pending: "Pendente (aguarda transferência)",
    paid_pending_invoice: "Paga (fatura pendente)",
    expired: "Expirada",
    cancelled: "Cancelada",
    none: "Sem subscrição",
};

export default function SuperAdminPage() {
    const [tab, setTab] = useState("companies");
    const [stats, setStats] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [filter, setFilter] = useState("");
    const [subFilter, setSubFilter] = useState("");
    const nav = useNavigate();
    const importInputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [s, c, subs] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/companies"),
                api.get("/admin/subscriptions"),
            ]);
            setStats(s.data);
            setCompanies(c.data);
            setSubscriptions(subs.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const activate = async (subId) => {
        if (!window.confirm("Ativar esta subscrição? A duração começa a contar agora.")) return;
        try { await api.post(`/admin/subscriptions/${subId}/activate`, { notes: "Ativada manualmente" }); toast.success("Subscrição ativada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const markPaid = (sub) => openMarkPaid(sub);

    // mark-paid modal state: { sub, transfer_date, notes, proof_file, proof_uploaded, submitting }
    const [markPaidModal, setMarkPaidModal] = useState(null);

    const openMarkPaid = (sub) => {
        const today = new Date().toISOString().slice(0, 10);
        setMarkPaidModal({ sub, transfer_date: today, notes: "", proof_file: null, proof_uploaded: !!sub.payment_proof_path, submitting: false });
    };

    const submitMarkPaid = async () => {
        if (!markPaidModal) return;
        setMarkPaidModal((m) => ({ ...m, submitting: true }));
        try {
            // 1) Upload proof first if user picked a file (best-effort but treated as required if provided).
            if (markPaidModal.proof_file) {
                const fd = new FormData();
                fd.append("file", markPaidModal.proof_file);
                await api.post(`/admin/subscriptions/${markPaidModal.sub.id}/proof`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            // 2) Now mark as paid (triggers Moloni + activation)
            const { data: res } = await api.post(
                `/admin/subscriptions/${markPaidModal.sub.id}/mark-paid`,
                { transfer_date: markPaidModal.transfer_date || null, notes: markPaidModal.notes || null },
            );
            if (res.status === "active") toast.success("Pagamento confirmado, fatura emitida e subscrição ativa");
            else toast.warning(`Pagamento confirmado mas fatura falhou: ${res.invoice_error || "ver logs"}. Use 'Reemitir fatura' depois.`);
            setMarkPaidModal(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
            setMarkPaidModal((m) => (m ? { ...m, submitting: false } : null));
        }
    };

    const issueInvoice = async (subId) => {
        try {
            const { data: res } = await api.post(`/admin/subscriptions/${subId}/issue-invoice`);
            if (res.skipped) toast.info("Fatura já tinha sido emitida");
            else toast.success("Fatura emitida com sucesso");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const extend = async (subId) => {
        const days = parseInt(window.prompt("Quantos dias adicionar?", "30"));
        if (!days || days <= 0) return;
        try { await api.post(`/admin/subscriptions/${subId}/extend`, { days }); toast.success("Subscrição estendida"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const cancel = async (subId) => {
        const sub = subscriptions.find((s) => s.id === subId);
        const isPending = sub && (sub.status === "pending" || sub.status === "paid_pending_invoice");
        const msg = isPending
            ? "Cancelar esta subscrição pendente? A empresa é notificada e o pedido fica arquivado."
            : "Cancelar subscrição? A empresa deixa de poder criar/editar.";
        if (!window.confirm(msg)) return;
        try { await api.post(`/admin/subscriptions/${subId}/cancel`); toast.success("Subscrição cancelada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const onImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            const text = await file.text();
            const payload = JSON.parse(text);
            const { data: res } = await api.post("/admin/companies/import", payload);
            toast.success(`Importação: empresa ${res.company}, ${res.users_created} utilizador(es) criado(s), ${res.users_skipped} já existentes, subscrição ${res.subscription}`);
            load();
        } catch (err) {
            toast.error(err?.response ? formatApiError(err) : "JSON inválido ou erro de leitura");
        }
    };

    const filtered = companies.filter((c) => !filter || (c.name || "").toLowerCase().includes(filter.toLowerCase()) || (c.email || "").toLowerCase().includes(filter.toLowerCase()));

    return (
        <div className="space-y-10" data-testid="super-admin-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="label-caps">Administração da Plataforma</div>
                    <h1 className="font-display text-5xl text-[#0d1628] mt-4">Central do Super Admin</h1>
                    <p className="text-[#52525b] mt-3 max-w-2xl">Supervisão de todas as empresas, utilizadores, projetos e subscrições na plataforma IHTP.</p>
                </div>
                <div>
                    <input
                        ref={importInputRef}
                        type="file"
                        accept="application/json,.json"
                        onChange={onImportFile}
                        className="hidden"
                        data-testid="import-company-input"
                    />
                    <button
                        onClick={() => importInputRef.current?.click()}
                        className="btn-outline"
                        data-testid="import-company-btn"
                        title="Importa uma empresa exportada (empresa + utilizadores + subscrição)"
                    >
                        <Upload size={14} /> Importar empresa (JSON)
                    </button>
                </div>
            </div>

            {/* STATS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
                {[
                    { icon: Building2, label: "Empresas", value: stats?.companies_total ?? "—" },
                    { icon: Users, label: "Utilizadores", value: stats?.users_total ?? "—" },
                    { icon: FolderKanban, label: "Projetos", value: stats?.projects_total ?? "—" },
                    { icon: CreditCard, label: "Subscrições ativas", value: stats?.subscriptions_active ?? "—" },
                ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="card-flat" data-testid={`admin-stat-${label}`}>
                        <Icon size={18} className="text-[#b89b6f]" />
                        <div className="label-caps mt-4">{label}</div>
                        <div className="font-display text-5xl text-[#0d1628] mt-3">{value}</div>
                    </div>
                ))}
            </div>

            {/* TABS */}
            <div className="flex gap-2 border-b border-[#e4e4e7] overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="admin-tabs">
                {[
                    { k: "companies", label: "Empresas" },
                    { k: "subscriptions", label: "Subscrições" },
                    { k: "pending", label: `Pendentes (${subscriptions.filter(s => s.status === "pending" || s.status === "paid_pending_invoice").length})` },
                    { k: "diagnostics", label: "Diagnóstico de Acesso" },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${tab === t.k ? "border-[#0d1628] text-[#0d1628] font-medium" : "border-transparent text-[#52525b] hover:text-[#0d1628]"}`}
                        data-testid={`tab-${t.k}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "companies" && (
                <section>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <input
                            placeholder="Pesquisar empresa..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="input-clean max-w-md"
                            data-testid="company-filter"
                        />
                        <span className="label-caps">{filtered.length} empresa(s)</span>
                    </div>
                    <div className="card-flat p-0 overflow-x-auto" data-testid="companies-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#e4e4e7]">
                                    <th className="text-left p-4 label-caps">Empresa</th>
                                    <th className="text-left p-4 label-caps">Subscrição</th>
                                    <th className="text-left p-4 label-caps">Fim</th>
                                    <th className="text-center p-4 label-caps">Utilizadores</th>
                                    <th className="text-center p-4 label-caps">Projetos</th>
                                    <th className="text-left p-4 label-caps">Criada</th>
                                    <th className="text-right p-4 label-caps"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.id} onClick={() => nav(`/app/super/companies/${c.id}`)} className="border-b border-[#e4e4e7] last:border-0 hover:bg-[#f9f9fa] cursor-pointer" data-testid={`admin-company-row-${c.id}`}>
                                        <td className="p-4">
                                            <div className="text-[#0d1628] font-medium">{c.name}</div>
                                            <div className="text-xs text-[#52525b]">{c.email}</div>
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs border px-2 py-1 capitalize ${STATUS_COLORS[c.subscription?.status] || STATUS_COLORS.none}`}>
                                                {c.subscription?.status === "active" ? `Ativa · ${c.subscription.plan_name || ""}` : c.subscription?.status || "Sem"}
                                            </span>
                                        </td>
                                        <td className="p-4 text-[#52525b]">{c.subscription?.ends_at ? c.subscription.ends_at.slice(0, 10) : "—"}</td>
                                        <td className="p-4 text-center text-[#0d1628]">{c.users_count}</td>
                                        <td className="p-4 text-center text-[#0d1628]">{c.projects_count}</td>
                                        <td className="p-4 text-[#52525b]">{c.created_at?.slice(0, 10)}</td>
                                        <td className="p-4 text-right text-[#52525b]"><ChevronRight size={16} /></td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan="7" className="p-8 text-center text-[#52525b]">Nenhuma empresa encontrada.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {(tab === "subscriptions" || tab === "pending") && (
                <section>
                    <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="subs-filter-bar">
                        <div className="relative flex-1 max-w-md">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b] pointer-events-none" />
                            <input
                                placeholder="Pesquisar por referência, empresa ou plano…"
                                value={subFilter}
                                onChange={(e) => setSubFilter(e.target.value)}
                                className="input-clean w-full pl-9"
                                data-testid="subs-filter-input"
                            />
                        </div>
                        {subFilter && (
                            <button onClick={() => setSubFilter("")} className="btn-ghost text-xs" data-testid="subs-filter-clear">Limpar</button>
                        )}
                    </div>
                    <div className="card-flat p-0 overflow-x-auto" data-testid="subs-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#e4e4e7]">
                                    <th className="text-left p-4 label-caps">Empresa</th>
                                    <th className="text-left p-4 label-caps">Plano</th>
                                    <th className="text-left p-4 label-caps">Estado</th>
                                    <th className="text-left p-4 label-caps">Método</th>
                                    <th className="text-left p-4 label-caps">Pedido</th>
                                    <th className="text-left p-4 label-caps">Fim</th>
                                    <th className="text-right p-4 label-caps">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptions
                                    .filter((s) => tab === "pending" ? (s.status === "pending" || s.status === "paid_pending_invoice") : true)
                                    .filter((s) => {
                                        if (!subFilter.trim()) return true;
                                        const q = subFilter.toLowerCase();
                                        return (
                                            (s.payment_reference || "").toLowerCase().includes(q) ||
                                            (s.company_name || "").toLowerCase().includes(q) ||
                                            (s.plan || "").toLowerCase().includes(q) ||
                                            (s.id || "").toLowerCase().includes(q)
                                        );
                                    })
                                    .map((s) => (
                                    <tr key={`${s.id}-${s.status}-${s.purchased_at || ""}`} className="border-b border-[#e4e4e7] last:border-0" data-testid={`admin-sub-row-${s.id}`}>
                                        <td className="p-4 text-[#0d1628]">
                                            {s.company_name || "—"}
                                            {s.payment_reference && (
                                                <div className="text-xs text-[#52525b] font-mono mt-1">{s.payment_reference}</div>
                                            )}
                                        </td>
                                        <td className="p-4 text-[#0d1628]">{s.plan}</td>
                                        <td className="p-4">
                                            <span className={`text-xs border px-2 py-1 ${STATUS_COLORS[s.status] || STATUS_COLORS.none}`}>
                                                {STATUS_LABELS[s.status] || s.status}
                                            </span>
                                        </td>
                                        <td className="p-4 text-[#52525b] capitalize">{(s.payment_method || "—").replace("_", " ")}</td>
                                        <td className="p-4 text-[#52525b]">{s.purchased_at?.slice(0, 10)}</td>
                                        <td className="p-4 text-[#52525b]">{s.ends_at ? s.ends_at.slice(0, 10) : "—"}</td>
                                        <td className="p-4 text-right whitespace-nowrap">
                                            {s.status === "pending" && (
                                                <>
                                                    <button onClick={() => markPaid(s)} className="btn-ghost text-[#0d1628]" data-testid={`mark-paid-${s.id}`} title="Confirmar receção da transferência bancária e emitir fatura">
                                                        <Landmark size={14} /> Pagamento recebido
                                                    </button>
                                                    <button onClick={() => activate(s.id)} className="btn-ghost text-[#52525b]" data-testid={`activate-${s.id}`} title="Ativar sem confirmar pagamento (uso excecional)">
                                                        <CheckCircle2 size={14} /> Forçar ativar
                                                    </button>
                                                    <button onClick={() => cancel(s.id)} className="btn-ghost text-red-600" data-testid={`cancel-${s.id}`} title="Cancelar esta inscrição pendente — o cliente não é cobrado.">
                                                        Cancelar
                                                    </button>
                                                </>
                                            )}
                                            {s.status === "paid_pending_invoice" && (
                                                <>
                                                    <button onClick={() => issueInvoice(s.id)} className="btn-ghost text-[#0d1628]" data-testid={`issue-invoice-${s.id}`} title="Tentar emitir a fatura Moloni novamente">
                                                        <CreditCard size={14} /> Reemitir fatura
                                                    </button>
                                                    <button onClick={() => activate(s.id)} className="btn-ghost text-[#52525b]" data-testid={`activate-${s.id}`} title="Forçar ativação sem aguardar pela fatura">
                                                        <CheckCircle2 size={14} /> Ativar mesmo assim
                                                    </button>
                                                    <button onClick={() => cancel(s.id)} className="btn-ghost text-red-600" data-testid={`cancel-${s.id}`} title="Cancelar esta subscrição já paga (uso excecional)">
                                                        Cancelar
                                                    </button>
                                                </>
                                            )}
                                            {s.status === "active" && (
                                                <>
                                                    <button onClick={() => extend(s.id)} className="btn-ghost" data-testid={`extend-${s.id}`}>
                                                        <Plus size={14} /> Estender
                                                    </button>
                                                    <button onClick={() => cancel(s.id)} className="btn-ghost text-red-600" data-testid={`cancel-${s.id}`}>
                                                        Cancelar
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {subscriptions.filter((s) => tab === "pending" ? (s.status === "pending" || s.status === "paid_pending_invoice") : true).length === 0 && (
                                    <tr><td colSpan="7" className="p-8 text-center text-[#52525b]">Nenhuma subscrição {tab === "pending" ? "pendente" : ""}.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {tab === "diagnostics" && <AccessDiagnostics />}

            {markPaidModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1628]/70 backdrop-blur-sm p-4" data-testid="mark-paid-modal" onClick={() => !markPaidModal.submitting && setMarkPaidModal(null)}>
                    <div className="bg-white border border-[#e4e4e7] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="px-8 pt-8 pb-6 border-b border-[#e4e4e7]">
                            <div className="flex items-center gap-3">
                                <Landmark size={24} className="text-[#b89b6f]" />
                                <h2 className="font-display text-3xl text-[#0d1628]" data-testid="mark-paid-modal-title">Confirmar pagamento</h2>
                            </div>
                            <p className="text-[#52525b] mt-4 text-sm leading-relaxed">
                                Empresa <strong className="text-[#0d1628]">{markPaidModal.sub.company_name || "—"}</strong> · plano <strong className="text-[#0d1628]">{markPaidModal.sub.plan}</strong>
                                {markPaidModal.sub.payment_reference && <> · ref. <span className="font-mono text-[#0d1628]">{markPaidModal.sub.payment_reference}</span></>}
                            </p>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="label-caps">Data da transferência</label>
                                <input
                                    type="date"
                                    value={markPaidModal.transfer_date}
                                    onChange={(e) => setMarkPaidModal((m) => ({ ...m, transfer_date: e.target.value }))}
                                    className="input-clean mt-2 w-full"
                                    max={new Date().toISOString().slice(0, 10)}
                                    data-testid="mark-paid-date"
                                />
                            </div>
                            <div>
                                <label className="label-caps">Notas internas (opcional)</label>
                                <textarea
                                    value={markPaidModal.notes}
                                    onChange={(e) => setMarkPaidModal((m) => ({ ...m, notes: e.target.value }))}
                                    rows={3}
                                    placeholder="Ex.: TPA balcão, ou referência interna do extrato bancário"
                                    className="input-clean mt-2 w-full"
                                    data-testid="mark-paid-notes"
                                />
                            </div>
                            <div>
                                <label className="label-caps">Comprovativo (opcional, PDF ou imagem)</label>
                                <label className="mt-2 flex items-center gap-3 border border-dashed border-[#e4e4e7] hover:border-[#b89b6f] cursor-pointer p-4 transition-colors" data-testid="mark-paid-proof-label">
                                    <Paperclip size={16} className="text-[#b89b6f] shrink-0" />
                                    <span className="text-sm text-[#0d1628] flex-1 truncate">
                                        {markPaidModal.proof_file
                                            ? `${markPaidModal.proof_file.name} (${(markPaidModal.proof_file.size / 1024).toFixed(0)} KB)`
                                            : markPaidModal.proof_uploaded
                                            ? "Comprovativo já carregado — substitua selecionando outro ficheiro."
                                            : "Anexar PDF ou imagem (máx. 10 MB)"}
                                    </span>
                                    <input
                                        type="file"
                                        accept="application/pdf,image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        data-testid="mark-paid-proof-input"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (!f) return;
                                            if (f.size > 10 * 1024 * 1024) { toast.error("Ficheiro maior que 10MB"); return; }
                                            setMarkPaidModal((m) => ({ ...m, proof_file: f }));
                                        }}
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-[#52525b] leading-relaxed">
                                Vamos emitir a fatura Moloni e ativar a subscrição automaticamente. Se a emissão falhar, a subscrição fica em <em>Paga (fatura pendente)</em> e pode reemitir manualmente.
                            </p>
                        </div>
                        <div className="px-8 pb-8 pt-4 border-t border-[#e4e4e7] bg-[#f9f9fa] flex gap-3 justify-end">
                            <button onClick={() => setMarkPaidModal(null)} disabled={markPaidModal.submitting} className="btn-outline" data-testid="mark-paid-cancel">Cancelar</button>
                            <button onClick={submitMarkPaid} disabled={markPaidModal.submitting} className="btn-primary" data-testid="mark-paid-submit">
                                {markPaidModal.submitting ? "A processar…" : "Confirmar pagamento"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


/**
 * Support tool: look up a user by email, view their status (soft-deleted,
 * subscription, recent login attempts, previous reset tokens) and generate a
 * fresh password-reset URL to share manually when emails don't arrive.
 * Rendered as a tab inside SuperAdminPage.
 */
function AccessDiagnostics() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [info, setInfo] = useState(null);
    const [resetUrl, setResetUrl] = useState(null);

    const lookup = async () => {
        const q = email.trim().toLowerCase();
        if (!q) return;
        setBusy(true); setInfo(null); setResetUrl(null);
        try {
            const r = await api.get(`/admin/users/lookup`, { params: { email: q } });
            setInfo(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const generateLink = async () => {
        const q = email.trim().toLowerCase();
        if (!q) return;
        setBusy(true);
        try {
            const r = await api.post(`/admin/users/password-reset-link`, { email: q });
            setResetUrl(r.data.reset_url);
            toast.success("Link de reset gerado. Copie e envie ao utilizador.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(resetUrl);
            toast.success("Link copiado");
        } catch { toast.error("Não foi possível copiar"); }
    };

    return (
        <section className="space-y-6" data-testid="access-diagnostics">
            <div className="border border-[#e4e4e7] bg-white p-6">
                <h2 className="font-display text-2xl text-[#0d1628]">Diagnóstico de acesso</h2>
                <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
                    Utilize esta ferramenta quando um utilizador reporta que <strong>não consegue entrar</strong> ou <strong>não recebeu o email de reset</strong>. Vê o estado da conta e gera um link de reset que pode partilhar por WhatsApp / telefone se o email não chegar.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="email do utilizador"
                        className="input-clean flex-1 min-w-[240px]"
                        data-testid="diagnostics-email-input"
                        disabled={busy}
                        onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
                    />
                    <button className="btn-outline" onClick={lookup} disabled={busy} data-testid="diagnostics-lookup-btn">
                        Consultar
                    </button>
                    <button className="btn-primary" onClick={generateLink} disabled={busy} data-testid="diagnostics-reset-btn">
                        Gerar link de reset
                    </button>
                </div>
            </div>

            {resetUrl && (
                <div className="border border-[#c7e8d9] bg-[#eef8f3] p-6" data-testid="reset-url-panel">
                    <div className="label-caps text-[#118567] mb-2">Link de reset (válido 1h)</div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <code className="flex-1 min-w-[300px] text-sm text-[#0d1628] break-all bg-white border border-[#e4e4e7] p-3 font-mono">
                            {resetUrl}
                        </code>
                        <button className="btn-outline" onClick={copyToClipboard} data-testid="reset-url-copy">
                            Copiar
                        </button>
                    </div>
                    <p className="text-xs text-[#52525b] mt-3">
                        Partilhe este endereço com o utilizador apenas por canais confiáveis. Ao clicar, poderá definir uma nova palavra-passe.
                    </p>
                </div>
            )}

            {info && !info.found && (
                <div className="border border-[#f5cfcf] bg-[#faeeee] p-4" data-testid="user-not-found">
                    <span className="text-sm text-[#8a1c1c]">
                        Utilizador <strong>{info.email}</strong> não foi encontrado nesta plataforma. Verifique se o email está correto ou se a conta foi criada noutro ambiente.
                    </span>
                </div>
            )}

            {info?.found && (
                <div className="border border-[#e4e4e7] bg-white p-6 space-y-4" data-testid="user-info-panel">
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="label-caps">Nome</div>
                            <div className="text-[#0d1628] mt-1">{info.user.name || "—"}</div>
                        </div>
                        <div>
                            <div className="label-caps">Email</div>
                            <div className="text-[#0d1628] mt-1">{info.user.email}</div>
                        </div>
                        <div>
                            <div className="label-caps">Papel</div>
                            <div className="text-[#0d1628] mt-1">{info.user.role}</div>
                        </div>
                        <div>
                            <div className="label-caps">Estado da conta</div>
                            <div className="mt-1">
                                {info.user.deleted_at
                                    ? <span className="text-[#dc2626]">Soft-deleted em {String(info.user.deleted_at).slice(0, 10)}</span>
                                    : <span className="text-[#118567]">Ativa</span>}
                            </div>
                        </div>
                        <div>
                            <div className="label-caps">Empresa</div>
                            <div className="text-[#0d1628] mt-1">{info.company?.name || "—"}</div>
                        </div>
                        <div>
                            <div className="label-caps">Subscrição</div>
                            <div className="text-[#0d1628] mt-1">{info.subscription?.status || "—"}</div>
                        </div>
                    </div>

                    {info.recent_login_attempts?.length > 0 && (
                        <div>
                            <div className="label-caps mb-2">Tentativas de login recentes</div>
                            <div className="overflow-x-auto border border-[#e4e4e7]">
                                <table className="w-full text-xs">
                                    <thead className="bg-[#faf6ea]">
                                        <tr>
                                            <th className="text-left p-2">Quando</th>
                                            <th className="text-left p-2">Sucesso</th>
                                            <th className="text-left p-2">IP</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {info.recent_login_attempts.map((a, i) => (
                                            <tr key={i} className="border-t border-[#e4e4e7]">
                                                <td className="p-2 text-[#52525b]">{String(a.ts).slice(0, 19).replace("T", " ")}</td>
                                                <td className="p-2">{a.success ? "✓" : "✗"}</td>
                                                <td className="p-2 font-mono text-[#71717a]">{a.ip || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {info.recent_reset_tokens?.length > 0 && (
                        <div>
                            <div className="label-caps mb-2">Tokens de reset gerados</div>
                            <ul className="text-xs text-[#52525b] space-y-1">
                                {info.recent_reset_tokens.map((t, i) => (
                                    <li key={i}>
                                        expira em {String(t.expires_at).slice(0, 19).replace("T", " ")}
                                        {" · "}{t.used ? "usado" : "por usar"}
                                        {t.generated_by_admin && " · (gerado por admin)"}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
